import { useQuery, useInfiniteQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../stores/app'
import {
  createVisualTask,
  listVisualTasks,
  getVisualTask,
  getTaskImages,
  getContentImages,
  cancelVisualTask,
  reejecutarVisualTask,
  getVisualSummary,
  editVisualImage,
  ApiError,
  type VisualTaskCreateRequest,
  type GeneratedImage,
  type EditVisualImageRequest,
} from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { toast } from 'sonner'
import { useRef, useEffect, useMemo } from 'react'

const VISUAL_TASKS_PAGE_SIZE = 20

interface UseVisualTasksFilters {
  status?: string
  limit?: number
  offset?: number
}

export function useVisualTasks(filters?: UseVisualTasksFilters) {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()
  const previousTasksRef = useRef<Record<string, string>>({})
  const statusFilter = filters?.status

  const query = useInfiniteQuery({
    queryKey: ['visual_tasks', organizationId, statusFilter],
    queryFn: async ({ pageParam }) => {
      if (!organizationId) return []
      return await listVisualTasks({
        status: statusFilter,
        limit: VISUAL_TASKS_PAGE_SIZE,
        offset: pageParam as number,
      })
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < VISUAL_TASKS_PAGE_SIZE) return undefined
      return allPages.length * VISUAL_TASKS_PAGE_SIZE
    },
    enabled: !!organizationId,
    refetchInterval: (queryState) => {
      const data = queryState?.state?.data
      const tasks = data?.pages?.flat() ?? []
      const hasPendingOrRunning = tasks.some(
        (task) => task.status === 'pending' || task.status === 'running'
      )
      return hasPendingOrRunning ? 5000 : false
    },
  })

  const tasks = useMemo(() => query.data?.pages.flat() ?? [], [query.data])

  // Detect task completion and show notifications
  useEffect(() => {
    tasks.forEach((task) => {
      const previousStatus = previousTasksRef.current[task.id]

      if (previousStatus === 'running' && task.status === 'completed') {
        toast.success('Visual generado', {
          description: 'Las imágenes se generaron exitosamente',
        })
      }

      if (
        (previousStatus === 'running' || previousStatus === 'pending') &&
        task.status === 'failed'
      ) {
        toast.error('Generación fallida', {
          description: 'La generación visual falló',
        })
      }

      previousTasksRef.current[task.id] = task.status
    })
  }, [tasks])

  const createMutation = useMutation({
    mutationFn: async (task: VisualTaskCreateRequest) => {
      if (!organizationId) throw new Error('No organization ID')
      
      return await createVisualTask(task)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visual_tasks', organizationId, statusFilter] })
      queryClient.invalidateQueries({ queryKey: ['visual_summary', organizationId] })
      toast.success('Tarea visual creada', {
        description: 'La generación visual ha comenzado',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.status === 402) {
          toast.error('Presupuesto excedido', {
            description: error.message,
          })
        } else {
          toast.error('Error al crear la tarea visual', {
            description: error.message,
          })
        }
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al crear la tarea visual', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const cancelMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await cancelVisualTask(taskId)
      return { id: taskId, status: 'cancelled' }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visual_tasks', organizationId, statusFilter] })
      queryClient.invalidateQueries({ queryKey: ['visual_summary', organizationId] })
      toast.success('Tarea cancelada', {
        description: 'La tarea visual se ha cancelado correctamente',
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error('Error al cancelar la tarea', {
          description: error.message,
        })
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al cancelar la tarea', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const retryMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await reejecutarVisualTask(taskId)
    },
    onSuccess: (_, taskId) => {
      queryClient.invalidateQueries({ queryKey: ['visual_tasks', organizationId, statusFilter] })
      queryClient.invalidateQueries({ queryKey: ['visual_task', taskId] })
      queryClient.invalidateQueries({ queryKey: ['task_images', taskId] })
      queryClient.invalidateQueries({ queryKey: ['visual_summary', organizationId] })
      toast.success('Tarea reenviada', {
        description: 'La generación visual se ha vuelto a poner en cola',
      })
    },
    onError: (error, _taskId) => {
      if (error instanceof ApiError) {
        toast.error('Error al reintentar la tarea', {
          description: error.message,
        })
        console.error('API Error:', error.message, error.status, error.data)
      } else {
        toast.error('Error al reintentar la tarea', {
          description: getErrorMessage(error),
        })
        console.error('Unexpected error:', error)
      }
    },
  })

  const editImageMutation = useMutation({
    mutationFn: async (request: EditVisualImageRequest) => {
      if (!organizationId) throw new Error('No organization ID')
      return await editVisualImage(request)
    },
    onSuccess: (_, variables) => {
      if (variables.visual_task_id) {
        queryClient.invalidateQueries({ queryKey: ['task_images', variables.visual_task_id] })
        queryClient.invalidateQueries({ queryKey: ['visual_task', variables.visual_task_id] })
      }
      if (variables.content_id) {
        queryClient.invalidateQueries({ queryKey: ['content_images', variables.content_id] })
      }
      queryClient.invalidateQueries({ queryKey: ['visual_tasks', organizationId, statusFilter] })
      queryClient.invalidateQueries({ queryKey: ['visual_summary', organizationId] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error('Error al editar la imagen', { description: error.message })
      } else {
        toast.error('Error al editar la imagen', {
          description: getErrorMessage(error),
        })
      }
    },
  })

  return {
    ...query,
    tasks,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    createTask: createMutation.mutateAsync,
    cancelTask: cancelMutation.mutateAsync,
    retryTask: retryMutation.mutateAsync,
    editImage: editImageMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isRetrying: retryMutation.isPending,
    isEditingImage: editImageMutation.isPending,
  }
}

export function useVisualTask(taskId: string | null) {
  return useQuery({
    queryKey: ['visual_task', taskId],
    queryFn: async () => {
      if (!taskId) return null
      
      return await getVisualTask(taskId)
    },
    enabled: !!taskId,
    refetchInterval: (query) => {
      // Poll every 5 seconds if task is pending or running
      const taskData = query?.state?.data
      const isPendingOrRunning = 
        taskData?.tarea?.status === 'pending' || taskData?.tarea?.status === 'running'
      return isPendingOrRunning ? 5000 : false
    },
  })
}

export function useTaskImages(taskId: string | null) {
  return useQuery({
    queryKey: ['task_images', taskId],
    queryFn: async () => {
      if (!taskId) return []
      
      return await getTaskImages(taskId)
    },
    enabled: !!taskId,
  })
}

/** First image per task for list thumbnails. Returns map of taskId -> first GeneratedImage or undefined. */
export function useTaskThumbnails(taskIds: string[]): Record<string, GeneratedImage | undefined> {
  const queries = useQueries({
    queries: taskIds.map((taskId) => ({
      queryKey: ['task_images', taskId] as const,
      queryFn: () => getTaskImages(taskId),
      enabled: !!taskId,
    })),
  })

  const thumbnails: Record<string, GeneratedImage | undefined> = {}
  queries.forEach((q, i) => {
    const id = taskIds[i]
    if (id && q.data?.length) thumbnails[id] = q.data[0]
  })
  return thumbnails
}

export function useContentImages(contentId: string | null) {
  return useQuery({
    queryKey: ['content_images', contentId],
    queryFn: async () => {
      if (!contentId) return []

      return await getContentImages(contentId)
    },
    enabled: !!contentId,
  })
}

/** Visual summary for Dashboard (total images per organization). */
export function useVisualSummary() {
  const organizationId = useAppStore((state) => state.organizationId)

  return useQuery({
    queryKey: ['visual_summary', organizationId],
    queryFn: async () => {
      if (!organizationId) return null
      return await getVisualSummary()
    },
    enabled: !!organizationId,
  })
}
